import type { TypeId } from '@unipile/db';
import type { ConsentScreenCheckpoint, ProviderAuthenticationIntentResponse } from '@unipile/types';
import { useId, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useSolveCheckpoint } from '../../../features/accounts/api/solve-checkpoint';
import { useProvider } from '../../../hooks/useProvider';
import { useScopesConsent } from '../../../hooks/useScopesConsent';
import { Button } from '../../shadcn/Button';
import { Checkbox } from '../../shadcn/Checkbox';
import { Field, FieldGroup } from '../../shadcn/Field';
import { Item, ItemActions, ItemContent, ItemDescription, ItemMedia, ItemTitle } from '../../shadcn/Item';
import { Spinner } from '../../shadcn/Spinner';
import { ScopesConsentContent } from '../ScopesConsentScreen';
import { WizardStepContainer } from '../containers/WizardStepContainer';

const RECRUITER_PRODUCT_ID = 'recruiter';
const SALES_NAVIGATOR_PRODUCT_ID = 'sales_navigator';

function getDefaultSelectedProducts(products: ConsentScreenCheckpoint['products']): string[] {
  const productIds = products.map(({ id }) => id);
  const hasRecruiterAndSalesNavigator =
    productIds.includes(RECRUITER_PRODUCT_ID) && productIds.includes(SALES_NAVIGATOR_PRODUCT_ID);

  return hasRecruiterAndSalesNavigator ? productIds.filter((id) => id !== RECRUITER_PRODUCT_ID) : productIds;
}

/**
 * A checkpoint that requires a selection of products to be activated.
 */
export function ConsentScreenCheckpoint({
  checkpoint,
  onSuccess,
  onError,
  intent_id,
}: {
  onSuccess: (response: ProviderAuthenticationIntentResponse) => void;
  onError: (error: unknown) => void;
  checkpoint: ConsentScreenCheckpoint;
  intent_id: TypeId<'account'>;
}) {
  const [selectedProducts, setSelectedProducts] = useState<string[]>(() => getDefaultSelectedProducts(checkpoint.products));
  const checkbox_group_id = useId();

  const { t } = useTranslation();
  const { product_icons, provider, provider_icon } = useProvider();
  const { scopes } = useScopesConsent({ provider });
  const hasRecruiterAndSalesNavigator =
    checkpoint.products.some(({ id }) => id === RECRUITER_PRODUCT_ID) &&
    checkpoint.products.some(({ id }) => id === SALES_NAVIGATOR_PRODUCT_ID);

  const { mutate, isPending } = useSolveCheckpoint({
    mutationConfig: {
      onSuccess,
      onError,
    },
  });

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (selectedProducts.length === 0) return;
    mutate({ data: { code: JSON.stringify(selectedProducts), intent_id } });
  };

  return (
    <WizardStepContainer
      title={t('CONSENT_SCREEN.title', { ns: provider })}
      description={t('CONSENT_SCREEN.description', { ns: provider })}
    >
      <form onSubmit={onSubmit}>
        <FieldGroup>
          <Field className="gap-2">
            {checkpoint.products.map(({ id, display_name, description, icon_url }, index) => {
              const checkbox_id = `${checkbox_group_id}-${index}`;

              return (
                <Item key={id} asChild variant="outline" size="sm">
                  <label
                    htmlFor={checkbox_id}
                    className="hover:bg-accent/50 has-[[data-state=checked]]:border-primary/50 has-[[data-state=checked]]:bg-primary/5 cursor-pointer"
                  >
                    {/* Centred on the name and its description, like the checkbox, rather than top-aligned. */}
                    <ItemMedia className="group-has-[[data-slot=item-description]]/item:translate-y-0 group-has-[[data-slot=item-description]]/item:self-center">
                      <img
                        src={icon_url ?? product_icons?.[id] ?? provider_icon}
                        alt=""
                        className="size-6 rounded-sm object-contain"
                      />
                    </ItemMedia>
                    <ItemContent className="gap-0">
                      <ItemTitle>{display_name}</ItemTitle>
                      {description && <ItemDescription className="text-xs leading-snug">{description}</ItemDescription>}
                    </ItemContent>
                    <ItemActions>
                      <Checkbox
                        id={checkbox_id}
                        checked={selectedProducts.includes(id)}
                        onCheckedChange={(checked) => {
                          setSelectedProducts((values) => {
                            const products = new Set(values);
                            if (checked) {
                              products.add(id);

                              if (hasRecruiterAndSalesNavigator && id === RECRUITER_PRODUCT_ID) {
                                products.delete(SALES_NAVIGATOR_PRODUCT_ID);
                              } else if (hasRecruiterAndSalesNavigator && id === SALES_NAVIGATOR_PRODUCT_ID) {
                                products.delete(RECRUITER_PRODUCT_ID);
                              }
                            } else {
                              products.delete(id);
                            }
                            return [...products.values()];
                          });
                        }}
                      />
                    </ItemActions>
                  </label>
                </Item>
              );
            })}
          </Field>

          <ScopesConsentContent scopes={scopes} />

          <Button className="mt-8 w-full" type="submit" disabled={selectedProducts.length === 0 || isPending}>
            {isPending && <Spinner />}
            {t('Common.next')}
          </Button>
        </FieldGroup>
      </form>
    </WizardStepContainer>
  );
}
